import styled from '@emotion/styled'
import { Product, WebLink } from '@news-mono/web-common'
import {
    breakpoint,
    calcRem,
    colors,
    fonts,
    themedValue,
} from '../../__styling'
import { StyledSharingWrap } from '../../bauhaus/BauhausHeroCard/BauhausHeroCard.styled'
import { StyledSharing } from '../../buttons/Sharing/Sharing.styled'
import { StyledKicker } from '../../cards/Kicker/Kicker.styled'
import { StyledArticleVideoContainer } from '../../publication/ArticleVideo/ArticleVideo.styled'
import { tokens } from '@news-mono/design-tokens'

export const StyledVideoPlayerWrapper = styled('div')(({ theme }) => ({
    width: '100%',
    height: '100%',
    flex: '1',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    display: 'block',
    //stops video from filling the whole container on load
    maxWidth: themedValue(theme, {
        fallback: '810px',
        sevennews: '100%',
    }),
    maxHeight: themedValue(theme, {
        sevennews: 'fit-content',
        fallback: '720px',
    }),
    borderRadius: theme.radius?.lg,
    overflow: theme.radius ? 'hidden' : undefined,
    [breakpoint('md')]: {
        maxWidth: themedValue(theme, {
            fallback: '810px',
            sevennews: '60%',
        }),
        flex: themedValue(theme, {
            fallback: '2',
            perthnow: undefined,
        }),
    },
}))

export const StyledVideoPlayer = styled('figure')(({ theme }) => ({
    width: '100%',
    height: 'auto',
    pointerEvents: 'auto',
    position: 'relative',
    padding: theme.kind === Product.PerthNow ? theme.margins.md : undefined,
    paddingTop: theme.kind === Product.PerthNow ? 0 : undefined,
    backgroundColor: theme.kind === Product.PerthNow ? 'white' : undefined,
    marginBottom: theme.kind === Product.PerthNow ? calcRem(16) : undefined,
    overflow: theme.kind === Product.PerthNow ? 'hidden' : undefined,
    borderRadius: theme.radius?.lg,
    borderBottom: themedValue(theme, {
        thewest: `${theme.colors.components?.videoHub?.borderBottom} 4px double`,
        perthnow: `${theme.colors.components?.videoHub?.borderBottom} 1px solid`,
        sevennews: undefined,
        fallback: undefined,
    }),
    [breakpoint('md')]: {
        padding: 0,
        paddingBottom: themedValue(theme, {
            thewest: calcRem(7),
            perthnow: calcRem(16),
            fallback: undefined,
        }),
    },

    '& [class*="StyledBrightcoveVideo"]': {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: 'auto',
        height: 'auto',
    },

    [`[class*="video-specificity"]`]: {
        width: '100%',
        height: 'auto',
        flex: '2',
        [breakpoint('md')]:
            theme.kind === Product.PerthNow
                ? undefined
                : {
                      flex: '2',
                  },
    },

    [`${StyledArticleVideoContainer}`]: {
        backgroundColor: 'transparent',
        marginBottom: 0,
    },
}))

export const StyledVideoPlayerTextContainer = styled('div')(({ theme }) => ({
    marginTop: calcRem(16),
    marginBottom: calcRem(16),
    paddingLeft: theme.kind === Product.PerthNow ? calcRem(8) : undefined,
    paddingRight: theme.kind === Product.PerthNow ? calcRem(8) : undefined,

    [breakpoint('sm')]: {
        marginTop: theme.kind === 'thewest' ? theme.margins.md : calcRem(8),
        marginBottom: theme.kind === 'thewest' ? calcRem(5) : 0,
    },
    [`${StyledKicker}`]: {
        color:
            theme.kind === 'sevennews'
                ? theme.sectionValues.primaryColor
                : undefined,
        fontSize: theme.kind === 'sevennews' ? calcRem(14) : undefined,
    },
}))

export const StyledVideoPlayerHeadline = styled('h1')(({ theme }) => ({
    fontSize: theme.kind === 'thewest' ? calcRem(18) : calcRem(21),
    fontFamily: themedValue(theme, {
        thewest: theme.fonts.serif,
        perthnow: theme.fonts.sansSerifCond,
        sevennews: undefined,
        fallback: undefined,
    }),
    lineHeight: theme.kind === 'thewest' ? calcRem(22) : '25px',
    marginTop: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    lineClamp: 2,
    fontWeight: theme.kind === 'thewest' ? 500 : undefined,
    '-webkit-line-clamp': '2',
    [breakpoint(theme.kind === Product.PerthNow ? 'xs' : 'sm')]: {
        fontSize: themedValue(theme, {
            thewest: calcRem(32),
            perthnow: calcRem(30),
            sevennews: calcRem(24),
            fallback: undefined,
        }),
        lineHeight: themedValue(theme, {
            thewest: calcRem(38),
            perthnow: calcRem(36),
            sevennews: calcRem(30),
            fallback: undefined,
        }),
        marginBottom: calcRem(8),
        fontWeight: theme.kind === 'thewest' ? 400 : undefined,
    },
    [breakpoint('md')]: {
        fontSize: themedValue(theme, {
            thewest: undefined,
            perthnow: calcRem(30),
            sevennews: calcRem(27),
            fallback: undefined,
        }),
        lineHeight: themedValue(theme, {
            thewest: undefined,
            perthnow: undefined,
            sevennews: calcRem(32),
            fallback: undefined,
        }),
    },
}))

export const StyledVideoPlayerDescription = styled('div')(({ theme }) => ({
    fontFamily: themedValue(theme, {
        thewest: undefined,
        perthnow: theme.fonts.sansSerif,
        sevennews: fonts.sevennews.headingAlternative,
        fallback: undefined,
    }),
    color:
        theme.kind === Product.PerthNow ? colors.perthnow.greyCoal : undefined,
    fontSize: theme.kind === 'thewest' ? calcRem(12) : calcRem(16),
    marginBottom: calcRem(16),
    minHeight: calcRem(20),
    fontWeight: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    lineClamp: 3,
    opacity: theme.kind === 'thewest' ? '80%' : undefined,
    '-webkit-line-clamp': '3',
    '-webkit-box-orient': 'vertical',
    [breakpoint('sm')]: {
        fontSize: theme.kind === 'thewest' ? calcRem(12) : calcRem(18),
        marginBottom: calcRem(8),
        minHeight: calcRem(45),
    },
    [breakpoint('md')]: {
        fontSize: themedValue(theme, {
            thewest: undefined,
            perthnow: calcRem(14),
            sevennews: calcRem(20),
            fallback: undefined,
        }),
        lineHeight: themedValue(theme, {
            thewest: calcRem(18),
            perthnow: calcRem(16),
            sevennews: calcRem(25),
            fallback: undefined,
        }),
    },
}))

export const StyledVideoPlayerDescriptionMoreLess = styled('div')(
    ({ theme }) => ({
        fontFamily: themedValue(theme, {
            sevennews: fonts.sevennews.headingAlternative,
            fallback: undefined,
        }),

        marginBottom: calcRem(16),
        minHeight: calcRem(20),
        fontWeight: 300,
        overflow: 'hidden',

        [breakpoint('sm')]: {
            fontSize: theme.kind === 'thewest' ? calcRem(12) : calcRem(18),
            marginBottom: calcRem(8),
            minHeight: calcRem(45),
        },
        [breakpoint('md')]: {
            marginBottom: themedValue(theme, {
                sevennews: calcRem(70),
                fallback: undefined,
            }),
            fontSize: themedValue(theme, {
                sevennews: calcRem(20),
                fallback: undefined,
            }),
            lineHeight: themedValue(theme, {
                sevennews: calcRem(25),
                fallback: undefined,
            }),
        },
    }),
)
export const StyledVideoPlayerDescriptionMoreLessButton = styled('button')({
    fontWeight: 500,
    cursor: 'pointer',
    padding: 0,
    margin: 0,
    background: 'none',
    border: 'none',
    color: colors.sevennews.bauhausBlack,
    [breakpoint('sm')]: {
        fontSize: calcRem(18),
    },
    [breakpoint('md')]: {
        fontSize: calcRem(20),
        lineHeight: calcRem(25),
    },
})

export const StyledVideoPlayerByline = styled('div')(({ theme }) => ({
    fontSize: calcRem(12),
    fontFamily: fonts.sevennews.headingAlternative,
    marginTop: 0,
    position: 'relative',
    display: theme.kind === Product.SevenNews ? undefined : 'none',
    fontWeight: 400,
    [breakpoint('md')]:
        theme.kind === Product.PerthNow
            ? undefined
            : {
                  position: 'absolute',
                  bottom: 0,
              },
    span: {
        color: colors.sevennews.dustyGray,
        margin: calcRem(0, 3),
    },
    [`${StyledSharingWrap}`]: {
        display: 'inline-block',
        position: 'absolute',
        bottom: themedValue(theme, {
            fallback: calcRem(-24),
            sevennews: calcRem(-32),
        }),

        [breakpoint('sm')]: {
            position: 'unset',
            verticalAlign: 'middle',
        },

        [`${StyledSharing}`]: {
            marginLeft: 0,

            '&::before': {
                display: 'none',
            },

            '& > button': {
                marginRight: calcRem(10),
                marginLeft: 0,
                // background: 'none',
                // svg: {
                //     fill: theme.sectionValues.primaryColor,
                // },
            },

            '& > button.gtm-clipboard-share-button:active:before': {
                opacity: 1,
                transition: '0s',
            },
            '& > button.gtm-clipboard-share-button:before': {
                position: 'absolute',
                top: calcRem(-35),
                left: calcRem(-31),
                width: calcRem(98),
                opacity: 0,
                padding: calcRem(6),
                content: '"✓ Link Copied "',
                transition: 'opacity 1.7s cubic-bezier(.0,.05,1,-0.24)',
                fontFamily: theme.fonts.sansSerif,
                fontSize: calcRem(12),
                fontWeight: 600,
                background: theme.colors.palette.black,
                color: theme.colors.palette.white,
                pointerEvents: 'none',
            },
        },
    },
}))
export const StyledVideoPlayerBreadcrumb = styled(WebLink)(({ theme }) => ({
    textDecoration: 'none',
    color: theme.sectionValues.primaryColor,
}))

export const StyledProfileLink = styled(WebLink)({
    color: tokens.sevennews.colors.palette.black,
    textDecoration: 'none',
    fontFamily: fonts.sevennews.headingAlternative,
})

export const StyledProfileLinkContainer = styled('div')(({ theme }) => ({
    display: theme.kind === Product.SevenNews ? 'inline-block' : 'none',
    marginRight: calcRem(12),
}))
